import React, {useRef, useState, useEffect} from "react";

const sections = [
  {key: "inclusion", label: "D&D is for everyone"},
  {key: "topic", label: "Stay on topic"},
  {key: "representation", label: "Respectful representation"},
  {key: "politics", label: "Refrain from political discussions"},
  {key: "table", label: "At the table"},
  {key: "enforcement", label: "Enforcement"},
];

const Conduct: React.FC = () => {
  const sectionRefs = {
    inclusion: useRef<HTMLDivElement>(null),
    topic: useRef<HTMLDivElement>(null),
    representation: useRef<HTMLDivElement>(null),
    politics: useRef<HTMLDivElement>(null),
    table: useRef<HTMLDivElement>(null),
    enforcement: useRef<HTMLDivElement>(null),
  };

  const [active, setActive] = useState("inclusion");

  useEffect(() => {
    const handleScroll = () => {
      const offsets = sections.map(({key}) => {
        const ref = sectionRefs[key as keyof typeof sectionRefs];
        if (ref.current) {
          const rect = ref.current.getBoundingClientRect();
          return {key, top: rect.top};
        }
        return {key, top: Infinity};
      });
      const visible = offsets.filter(
        (o) => o.top < window.innerHeight * 0.33 && o.top > -200
      );
      if (visible.length > 0) {
        setActive(visible[visible.length - 1].key);
      } else {
        setActive(sections[0].key);
      }
    };
    window.addEventListener("scroll", handleScroll, {passive: true});
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleSidebarClick = (key: string) => {
    const ref = sectionRefs[key as keyof typeof sectionRefs];
    if (ref && ref.current) {
      ref.current.scrollIntoView({behavior: "smooth", block: "start"});
    }
    setActive(key);
  };

  return (
    <div className="legal-page sidebar-layout">
      {/* Sidebar */}
      <aside className="sidebar-aside">
        <div className="sidebar-header">CATEGORIES</div>
        <nav>
          {sections.map((section) => (
            <button
              key={section.key}
              onClick={() => handleSidebarClick(section.key)}
              className={`sidebar-link${active === section.key ? " sidebar-link-active" : ""}`}
            >
              {section.label}
            </button>
          ))}
        </nav>
      </aside>

      {/* Main Content */}
      <div className="container sidebar-content">
        <h1 className="legal-title">Code of Conduct</h1>
        <div className="legal-date">Last updated: March 15, 2025</div>
        <p className="legal-intro">
          Our community guidelines for a safe and inclusive game environment
        </p>

        {/* 1. D&D is for everyone */}
        <section
          ref={sectionRefs.inclusion}
          className="legal-section legal-section-content"
        >
          <h2 className="legal-section-title">1. D&D is for everyone</h2>
          <p>
            We are dedicated to creating an inclusive and safe environment for
            all players. Any form of racism, sexism, homophobia, transphobia, or
            intolerance of any kind will not be accepted. Violations will result
            in a permanent ban from the League.
          </p>
        </section>
        <hr className="legal-divider" />

        {/* 2. Stay on topic */}
        <section
          ref={sectionRefs.topic}
          className="legal-section legal-section-content"
        >
          <h2 className="legal-section-title">2. Stay on topic</h2>
          <p>
            League communications and gatherings are primarily for organizing
            and discussing games. We ask that you keep conversations focused on
            this. For off-topic discussions, please use the appropriate channels
            or settings.
          </p>
        </section>
        <hr className="legal-divider" />

        {/* 3. Respectful representation */}
        <section
          ref={sectionRefs.representation}
          className="legal-section legal-section-content"
        >
          <h2 className="legal-section-title">3. Respectful representation</h2>
          <p>
            Please ensure your character names, descriptions, and any personal
            identifiers used within the League are appropriate. If anything is
            deemed offensive, you will be asked to change it.
          </p>
        </section>
        <hr className="legal-divider" />

        {/* 4. Refrain from political discussions */}
        <section
          ref={sectionRefs.politics}
          className="legal-section legal-section-content"
        >
          <h2 className="legal-section-title">
            4. Refrain from political discussions
          </h2>
          <p>
            This is not the place for political debate, and the world of D&D
            exists outside of real-world politics. Please refrain from political
            discussions during League activities and gameplay.
          </p>
        </section>
        <hr className="legal-divider" />

        {/* 5. At the table */}
        <section
          ref={sectionRefs.table}
          className="legal-section legal-section-content"
        >
          <h2 className="legal-section-title">5. At the table</h2>
          <p>
            All general rules apply during gameplay, along with any specific
            table or home rules set by the DM. If you're invited to play in
            someone's home or venue, be respectful and courteous to both the
            host and other players.
          </p>
        </section>
        <hr className="legal-divider" />

        {/* 6. Enforcement */}
        <section
          ref={sectionRefs.enforcement}
          className="legal-section legal-section-content"
        >
          <h2 className="legal-section-title">6. Enforcement</h2>
          <p>Violations of this code may result in:</p>
          <ul className="legal-list">
            <li>Warnings and corrective guidance</li>
            <li>Temporary suspension from sessions</li>
            <li>Removal from specific campaigns</li>
            <li>Restrictions on participation</li>
            <li>Permanent removal from the League</li>
          </ul>
          <p>
            Enforcement decisions are made by League administrators after
            careful review of incidents.
          </p>
        </section>
      </div>
    </div>
  );
};

export default Conduct;
